import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from '@/lib/api';

const BrandingContext = createContext(null);

const DEFAULTS = {
  brand_name: "Tap & Compass",
  brand_tagline: "Every Pour has a Story",
  brand_short_name: "T&C",
  brand_description: "Interactive digital menu for self-tap beer bars",
  site_title: "Tap & Compass",
  site_tagline: "Every Pour has a Story",
  brand_logo_url: "",
  brand_icon_url: "",
  brand_primary_color: "#f59e0b",
  brand_secondary_color: "#10b981",
};

export function BrandingProvider({ children }) {
  const [settings, setSettings] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get('/settings');
      setSettings(res.data || {});
    } catch {
      setSettings({});
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const brand = {
    brandName: settings?.brand_name || settings?.site_title || DEFAULTS.brand_name,
    brandTagline: settings?.brand_tagline || settings?.site_tagline || DEFAULTS.brand_tagline,
    brandShortName: settings?.brand_short_name || DEFAULTS.brand_short_name,
    brandDescription: settings?.brand_description || DEFAULTS.brand_description,
    siteTitle: settings?.site_title || settings?.brand_name || DEFAULTS.site_title,
    siteTagline: settings?.site_tagline || settings?.brand_tagline || DEFAULTS.site_tagline,
    logoUrl: settings?.brand_logo_url || DEFAULTS.brand_logo_url,
    iconUrl: settings?.brand_icon_url || DEFAULTS.brand_icon_url,
    primaryColor: settings?.brand_primary_color || DEFAULTS.brand_primary_color,
    secondaryColor: settings?.brand_secondary_color || DEFAULTS.brand_secondary_color,
    loaded: settings !== null,
    refresh: fetchSettings,
  };

  // Sync document title with brand name
  useEffect(() => {
    if (settings) {
      const title = settings.brand_name || settings.site_title || DEFAULTS.brand_name;
      document.title = title;
    }
  }, [settings]);

  return (
    <BrandingContext.Provider value={brand}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    // Fallback for components rendered outside the provider
    return {
      brandName: DEFAULTS.brand_name,
      brandTagline: DEFAULTS.brand_tagline,
      brandShortName: DEFAULTS.brand_short_name,
      brandDescription: DEFAULTS.brand_description,
      siteTitle: DEFAULTS.site_title,
      siteTagline: DEFAULTS.site_tagline,
      logoUrl: "",
      iconUrl: "",
      primaryColor: DEFAULTS.brand_primary_color,
      secondaryColor: DEFAULTS.brand_secondary_color,
      loaded: false,
      refresh: () => {},
    };
  }
  return ctx;
}
